import React from 'react'
import PropTypes from 'prop-types'
import {Button, Spin, Tag, Tooltip} from 'antd'
import {
    ATTENTION_LIMIT,
    GRADE_ORDER,
    attentionListOf,
    deltaOf,
    deltaToneOf,
    gradeBarLabelOf,
    gradeBreakdownOf,
    gradeLabelOf,
    gradeTagTextOf,
    previousRangeLabelOf,
} from '../../helpers/efficiencyReport'
import {DASH, formatDecimal, formatEfficiency, formatInt} from '../../helpers/format'

/**
 * Khối "Tổng quan hiệu suất đội ngũ" — nằm ngay dưới dải thẻ KPI của màn THEO NHÂN VIÊN.
 *
 * ⚠️ Chỉ có ở màn theo nhân viên, KHÔNG có ở màn theo công đoạn. Hạng là kết luận về một con người,
 *    mà màn theo công đoạn chỉ có ô tài khoản × công đoạn chứ không có tổng của từng người — dựng
 *    khối này ở đó là đếm một người ba lần vì họ làm ba công đoạn. Màn kia có biểu đồ công đoạn
 *    làm việc tương đương.
 *
 * ⚠️ Mặc định GẬP. Mở ra mới gọi lại API kèm `comparePrevious`, vì so kỳ trước tốn thêm một lượt
 *    đọc `itemhistories` (22,4 triệu doc) ở server. Mở khối mặc định là bắt mọi người trả giá cho
 *    một phần phần lớn không ai xem.
 */
function TeamEfficiencyPanel({actors, totals, thresholds, open, onToggle, loadingPrevious, onSelectActor}) {
    const counts = gradeBreakdownOf(actors)
    const attention = attentionListOf(actors, thresholds)
    const ranked = GRADE_ORDER.reduce((sum, key) => sum + counts[key], 0)
    /*
     * ⚠️ KHÔNG gác bằng `open`, và hai màn khác nhau là có lý — đọc chữ trên nút:
     *      · màn này ghi "Thu gọn" → nói về CHỖ. Chip chênh ở chú giải hạng tính từ dữ liệu ĐÃ có
     *        trên màn, không tốn thêm lượt đọc nào, đúng cùng lý do phân bố hạng vẫn hiện khi gập.
     *        Giấu chúng đi là vứt thứ vừa trả giá một lượt đọc `itemhistories` để lấy về.
     *      · màn theo công đoạn ghi "Ẩn so sánh với kỳ trước" → nói về SO SÁNH, nên ở đó biểu đồ
     *        phải ẩn lớp kỳ trước thật.
     *    Vòng review 2 chỉ báo lỗi ở biểu đồ màn kia; phần này tôi gác thêm vì tưởng "cùng lỗi",
     *    và đó là vá quá tay. Đổi bộ lọc sau khi gập sẽ không gửi `comparePrevious` nữa nên chip
     *    tự biến mất ở lượt tải kế — chúng luôn mô tả đúng dữ liệu đang nằm trên màn.
     */
    const previous = (totals && totals.previous) || null
    const previousRangeLabel = previousRangeLabelOf(totals)

    return (
        <div className="VcTeamPanel">
            <div className="VcTeamPanelHead">
                <span className="VcTeamPanelTitle">Tổng quan hiệu suất đội ngũ</span>
                <Button type="link" size="small" onClick={onToggle}>
                    {open ? 'Thu gọn' : 'Mở rộng · so với kỳ trước'}
                </Button>
                {/* Nói thẳng kỳ trước là kỳ nào — "cùng độ dài" không đủ để đoán ra 02/08–31/08. */}
                {open && previousRangeLabel && !loadingPrevious && (
                    <span className="VcMuted">Kỳ trước: {previousRangeLabel}</span>
                )}
                {loadingPrevious && <Spin size="small"/>}
            </div>

            {/*
              * Phân bố hạng hiện cả khi khối đang gập: nó tính từ dữ liệu ĐÃ có trên màn, không tốn
              * thêm lượt đọc nào. Chỉ phần so kỳ trước mới nằm sau nút mở rộng.
              */}
            <div className="VcGradeBar" role="img" aria-label={gradeBarLabelOf(counts, ranked)}>
                {GRADE_ORDER.map((key) => (counts[key] > 0 ? (
                    <Tooltip key={key} title={`Hạng ${key} — ${gradeLabelOf(key).title}: ${counts[key]} tài khoản`}>
                        <span
                            className={`VcGradeSeg VcGradeSeg--${key}`}
                            style={{flexGrow: counts[key]}}
                        />
                    </Tooltip>
                ) : null))}
            </div>

            <div className="VcGradeLegend">
                {GRADE_ORDER.map((key) => (
                    <span className="VcGradeLegendItem" key={key}>
                        {/*
                          * Chấm màu ĐỨNG CẠNH chữ, không tô màu lên chữ. Bốn token hạng là màu nền
                          * tươi; đặt làm màu chữ trên nền trắng thì hạng C (cam) gần như không đọc
                          * được ở màn hình xưởng.
                          */}
                        <i className={`VcGradeDot VcGradeDot--${key}`}/>
                        <span className="VcGradeLegendText">
                            {key === 'N' ? 'Chưa xếp hạng' : `${key} · ${gradeLabelOf(key).title}`}
                        </span>
                        <strong>{formatInt(counts[key])}</strong>
                        {previous && key !== 'N' ? (
                            <DeltaChip
                                value={deltaOf(counts[key], previous.grades ? previous.grades[key] : null)}
                                unit=""
                                // Thêm người hạng C/D là xấu; thêm người hạng A/B là tốt.
                                invert={key === 'C' || key === 'D'}
                            />
                        ) : null}
                    </span>
                ))}
            </div>

            {open && (
                <div className="VcTeamCompare">
                    {previous ? (
                        <>
                            <CompareRow
                                label="Hiệu suất chung"
                                current={totals.efficiency === null ? null : formatEfficiency(totals.efficiency)}
                                delta={deltaOf(totals.efficiency, previous.efficiency)}
                                unit=" điểm"
                                scale={100}
                            />
                            <CompareRow
                                label="Tài khoản xếp hạng được"
                                current={formatInt(totals.rankedActors)}
                                delta={deltaOf(totals.rankedActors, previous.rankedActors)}
                                unit=""
                                scale={1}
                            />
                        </>
                    ) : (
                        /*
                         * ⚠️ Nói rõ VÌ SAO trống. Khối vừa mở mà không có gì đọc như màn hình hỏng,
                         *    trong khi sự thật chỉ là kỳ trước không có dữ liệu để so.
                         */
                        <div className="VcMuted">
                            {loadingPrevious
                                ? 'Đang nạp số liệu kỳ trước…'
                                : 'Kỳ trước cùng độ dài chưa có sản lượng hoặc chưa nhập giờ — không có gì để so.'}
                        </div>
                    )}
                </div>
            )}

            {attention.length > 0 && (
                <div className="VcAttention">
                    <div className="VcAttentionHead">
                        Cần chú ý ({formatInt(attention.length)}
                        {attention.length === ATTENTION_LIMIT ? '+' : ''})
                    </div>
                    {attention.map((row) => {
                        const label = gradeLabelOf(row.efficiency.grade && row.efficiency.grade.grade)

                        return (
                            <button type="button" className="VcAttentionItem" key={row.actor} onClick={() => onSelectActor(row.actor)}>
                                <span className="VcAttentionActor">{row.actor}</span>
                                {/*
                                  * ⚠️ Hạng kèm CHỮ ở đây nữa (BR-3.3). Đây là chỗ người dùng nhìn
                                  *    nhanh nhất và là chỗ duy nhất hạng D xuất hiện theo tên
                                  *    người — để màu làm kênh duy nhất ở đúng chỗ đó thì in ra
                                  *    giấy là mất sạch phân biệt.
                                  */}
                                <Tag color={label.color}>
                                    {gradeTagTextOf(row.efficiency.grade && row.efficiency.grade.grade)}
                                </Tag>
                                <span className="VcAttentionValue">{formatEfficiency(row.efficiency.efficiency)}</span>
                            </button>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

/**
 * Chênh lệch so kỳ trước, có dấu. `null` thì không chiếm chỗ — không in "—" cạnh mọi con số.
 *
 * ⚠️ `invert` KHÔNG phải tuỳ chọn trang trí: với số người hạng C/D, TĂNG là tin XẤU. Bản trước tô
 *    màu thuần theo dấu nên "▲5" người hạng D hiện màu xanh thành công — màn hình chúc mừng đúng
 *    lúc xưởng có thêm năm người cần hỗ trợ. Mũi tên vẫn theo dấu (nó nói hướng), chỉ màu đảo (nó
 *    nói tốt hay xấu).
 */
const DeltaChip = ({value, unit, scale = 1, invert = false}) => {
    const tone = deltaToneOf(value, invert)

    if (tone === null) return null

    const up = value > 0

    return (
        <span className={`VcDeltaChip ${tone}`}>
            {up ? '▲' : '▼'}
            {formatDecimal(Math.abs(value) * scale, scale === 1 ? 0 : 1)}
            {unit}
        </span>
    )
}

const CompareRow = ({label, current, delta, unit, scale}) => (
    <div className="VcCompareRow">
        <span className="VcCompareLabel">{label}</span>
        <strong className="VcCompareValue">{current === null ? DASH : current}</strong>
        <DeltaChip value={delta} unit={unit} scale={scale}/>
        <span className="VcMuted">so với kỳ trước</span>
    </div>
)

TeamEfficiencyPanel.propTypes = {
    actors: PropTypes.array,
    totals: PropTypes.object,
    thresholds: PropTypes.object,
    open: PropTypes.bool,
    onToggle: PropTypes.func.isRequired,
    loadingPrevious: PropTypes.bool,
    onSelectActor: PropTypes.func.isRequired,
}

export default TeamEfficiencyPanel
